import { PAGE_SIZE } from '@/constants/pageSize';
import { useDeleteProject, useGetProjects } from '@/hooks/useProject';
import useTableControls from '@/hooks/useTableControls';
import Loader from '../ui/Loader';
import StyledTable from '../styled/StyledTable';
import {
  Project,
  getProjectsColumnDef,
} from '@/types/projects/projectsColumnDef';
import StyledNextPreviousButtons from '../styled/StyledNextPreviousButtons';
import { useCallback, useState } from 'react';
import ViewProjectModal from './ViewProjectModal';
import { useToast } from '@/hooks/useToast';
import ModalDeletion from '../ui/ModalDeletion';
import { useNavigate } from 'react-router';
import { PATHS } from '@/constants/Paths';
import { Row } from '@tanstack/react-table';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';

interface ListProjectsPageProps {
  clientId: string;
  isTourOperator?: boolean;
}

const ListProjectsPage = ({
  clientId,
  isTourOperator = false,
}: ListProjectsPageProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();
  const {
    data: projectsResponse,
    isSuccess,
    isLoading,
  } = useGetProjects(clientId, {
    page,
    pageSize: PAGE_SIZE,
    sortField,
    sortOrder,
  });

  const [projectToEditId, setProjectToEditId] = useState<string | null>(null);
  const [projectToDeleteId, setProjectToDeleteId] = useState<string | null>(
    null,
  );

  const { mutateAsync: deleteProject } = useDeleteProject(clientId);
  const { toastAsync } = useToast();

  const navigateToProject = useCallback(
    (row: Row<Project>) => {
      navigate(
        PATHS.distributors.clients.estimates.list
          .replace(':clientId', clientId)
          .replace(':projectId', row.original.id),
      );
    },
    [clientId, navigate],
  );

  return isLoading ? (
    <Loader />
  ) : (
    isSuccess && (
      <div className="flex flex-col w-full h-auto p-6 gap-6">
        <StyledTable
          columns={getTranslatedConst((t) =>
            getProjectsColumnDef(t, isTourOperator),
          )}
          data={projectsResponse.projects}
          getRowId={(row) => row.id}
          sorting={sorting}
          setSorting={setSorting}
          onViewRow={navigateToProject}
          onRowClick={navigateToProject}
          onEditRow={(row) => {
            setProjectToEditId(row.original.id);
          }}
          onDeleteRow={(row) => {
            setProjectToDeleteId(row.original.id);
          }}
        />
        <StyledNextPreviousButtons
          dataLength={projectsResponse.total}
          label="projet"
          page={page}
          setPage={setPage}
          pageSize={PAGE_SIZE}
        />
        {projectToEditId && (
          <ViewProjectModal
            clientId={clientId}
            projectId={projectToEditId}
            isTourOperator={isTourOperator}
            closeModal={() => {
              setProjectToEditId(null);
            }}
          />
        )}
        {projectToDeleteId && (
          <ModalDeletion
            actionOnClick={async () => {
              await toastAsync(deleteProject(projectToDeleteId), {
                success: t('project.projectDeletedSuccess'),
              });
            }}
            closeModal={() => {
              setProjectToDeleteId(null);
            }}
            type="project"
          />
        )}
      </div>
    )
  );
};

export default ListProjectsPage;
